import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useRegisterRefresh } from '../context/PullToRefreshContext';

const WEEKDAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const STATUS_LABEL = {
  completed: 'Tamamlandı',
  in_progress: 'Devam Ediyor',
  overdue: 'Gecikmiş',
};

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toYmd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function mondayFirstIndex(d) {
  return (d.getDay() + 6) % 7;
}

function startGridDate(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  const idx = mondayFirstIndex(first);
  const start = new Date(year, monthIndex, 1 - idx);
  start.setHours(12, 0, 0, 0);
  return start;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function statusBadgeClass(status) {
  if (status === 'completed') return 'bg-emerald-100 text-emerald-900 border-emerald-200 hover:bg-emerald-200';
  if (status === 'overdue') return 'bg-red-100 text-red-900 border-red-200 hover:bg-red-200';
  return 'bg-amber-100 text-amber-900 border-amber-200 hover:bg-amber-200';
}

function isVisitTask(task) {
  const cat = task.category_name || '';
  if (!cat.includes('Ziyaret')) return false;
  const dl = task.deadline;
  return Boolean(dl && String(dl).trim());
}

function formatDateTR(ymd) {
  try {
    const [y, m, d] = ymd.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });
  } catch {
    return ymd;
  }
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const now = new Date();
  const [cursor, setCursor] = useState(() => new Date(now.getFullYear(), now.getMonth(), 1));
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const year = cursor.getFullYear();
  const monthIndex = cursor.getMonth();

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.getTasks({ tab: 'all' });
      const list = Array.isArray(data) ? data : data.results || [];
      setTasks(list.filter(isVisitTask));
    } catch {
      setError('Ziyaretler yüklenirken bir hata oluştu.');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const byDate = useMemo(() => {
    const map = new Map();
    tasks.forEach((t) => {
      const key = String(t.deadline).slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    });
    return map;
  }, [tasks]);

  const gridDays = useMemo(() => {
    const start = startGridDate(year, monthIndex);
    const days = [];
    for (let i = 0; i < 42; i += 1) {
      days.push(addDays(start, i));
    }
    return days;
  }, [year, monthIndex]);

  // Arama sonuçları: başlık veya müşteri adı eşleşmesi, tarihe göre azalan sıra
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 2) return [];
    return tasks
      .filter((t) => {
        const title = (t.title || '').toLowerCase();
        const customer = (t.customer_name || '').toLowerCase();
        return title.includes(q) || customer.includes(q);
      })
      .sort((a, b) => String(b.deadline).localeCompare(String(a.deadline)));
  }, [tasks, searchQuery]);

  const isSearching = searchQuery.trim().length >= 2;

  const monthLabel = cursor.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });

  const goPrev = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const goNext = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const goThisMonth = () => setCursor(new Date(now.getFullYear(), now.getMonth(), 1));

  return (
    <div className="p-3 pb-6">

      {/* Arama kutusu */}
      <div className="relative mb-4">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
        </svg>
        <input
          type="text"
          className="input pl-9 pr-4"
          placeholder="Müşteri adı veya konu ara…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600 mb-3 text-center">{error}</p>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : isSearching ? (
        /* Arama sonuçları */
        <div>
          <p className="text-xs text-gray-500 mb-3">
            {searchResults.length > 0
              ? `${searchResults.length} ziyaret bulundu`
              : 'Eşleşen ziyaret bulunamadı'}
          </p>
          <div className="space-y-2">
            {searchResults.map((t) => {
              const ymd = String(t.deadline).slice(0, 10);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => navigate(`/satis/tasks/${t.id}`)}
                  className="w-full text-left bg-white rounded-xl border border-gray-200 px-4 py-3 shadow-sm active:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-brand-600 mb-0.5">{formatDateTR(ymd)}</p>
                      <p className="text-sm font-medium text-gray-900 leading-snug">{t.title}</p>
                      {t.customer_name && (
                        <p className="text-xs text-gray-500 mt-0.5">{t.customer_name}</p>
                      )}
                    </div>
                    <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusBadgeClass(t.status)}`}>
                      {STATUS_LABEL[t.status] || t.status}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        /* Takvim görünümü */
        <>
          <div className="flex items-center justify-between gap-2 mb-4">
            <button
              type="button"
              onClick={goPrev}
              className="shrink-0 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 shadow-sm active:bg-gray-50"
            >
              ‹ Önceki
            </button>
            <div className="min-w-0 text-center">
              <h1 className="text-base font-bold text-gray-900 capitalize truncate">{monthLabel}</h1>
            </div>
            <button
              type="button"
              onClick={goNext}
              className="shrink-0 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 shadow-sm active:bg-gray-50"
            >
              Sonraki ›
            </button>
          </div>

          <div className="flex justify-center mb-3">
            <button
              type="button"
              onClick={goThisMonth}
              className="px-4 py-2 rounded-xl bg-brand-500 text-white text-sm font-semibold shadow-sm active:bg-brand-600"
            >
              Bu Ay
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center py-1">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {gridDays.map((d) => {
              const ymd = toYmd(d);
              const inMonth = d.getMonth() === monthIndex;
              const dayTasks = byDate.get(ymd) || [];
              return (
                <div
                  key={ymd}
                  className={`min-h-[72px] sm:min-h-[88px] rounded-lg border p-0.5 flex flex-col ${
                    inMonth ? 'bg-white border-gray-200' : 'bg-gray-100/80 border-gray-100'
                  }`}
                >
                  <span
                    className={`text-[11px] font-semibold leading-tight px-0.5 ${
                      inMonth ? 'text-gray-800' : 'text-gray-400'
                    }`}
                  >
                    {d.getDate()}
                  </span>
                  <div className="flex-1 flex flex-col gap-0.5 min-h-0 overflow-hidden">
                    {dayTasks.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => navigate(`/satis/tasks/${t.id}`)}
                        className={`w-full text-left text-[9px] sm:text-[10px] leading-tight px-1 py-0.5 rounded border truncate font-medium transition-colors ${statusBadgeClass(
                          t.status
                        )}`}
                        title={t.title}
                      >
                        {t.title}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
